import { useEffect, useMemo, useRef, useState } from 'react';
import type { MapRoute, MapSettings, MapIcon, PosterSettings, PosterTextData } from '@/types/canvas';
import { MapCanvas } from './MapCanvas';
import {
  DEFAULT_POSTER_STYLE_ID,
  getPosterStyleDefinition,
  type PosterStyleDefinition,
  type PosterTextBackground,
  type PosterTextBlock,
} from '@/config/posterStyles';
import { cn } from '@/lib/utils';

interface PosterRendererProps {
  width: number;
  height: number;
  mapSettings: MapSettings;
  posterSettings: PosterSettings;
  textData: PosterTextData;
  mapIcons?: MapIcon[];
  selectedIconId?: string | null;
  selectedMapIconId?: string | null;
  selectedIconColor?: string;
  routes?: MapRoute[];
  onMapChange?: (centerLat: number, centerLng: number, zoom: number) => void;
  onMapClick?: (lat: number, lng: number) => void;
  onIconClick?: (iconId: string) => void;
  onIconUpdate?: (iconId: string, lat: number, lng: number) => void;
  onIconSelectCancel?: () => void;
  onMapInstanceReady?: (mapInstance: any) => void;
}

type TextRefMap = Record<string, HTMLDivElement | null>;

const SLOT_ORDER: Array<keyof PosterTextData> = ['title', 'subtitle', 'detail'];

export function PosterRenderer({
  width,
  height,
  mapSettings,
  posterSettings,
  textData,
  mapIcons = [],
  selectedIconId = null,
  selectedMapIconId = null,
  selectedIconColor = '#000000',
  routes = [],
  onMapChange,
  onMapClick,
  onIconClick,
  onIconUpdate,
  onIconSelectCancel,
  onMapInstanceReady,
}: PosterRendererProps) {
  const styleId =
    posterSettings.styleId || mapSettings.selectedStyleId || DEFAULT_POSTER_STYLE_ID;

  const styleDefinition = useMemo<PosterStyleDefinition>(() => {
    return getPosterStyleDefinition(styleId);
  }, [styleId]);

  const scale = useMemo(() => {
    const scaleX = width / styleDefinition.baseSize.width;
    const scaleY = height / styleDefinition.baseSize.height;
    return Math.min(scaleX, scaleY);
  }, [height, width, styleDefinition.baseSize.height, styleDefinition.baseSize.width]);

  const contentWidth = styleDefinition.baseSize.width * scale;
  const contentHeight = styleDefinition.baseSize.height * scale;
  const offsetX = (width - contentWidth) / 2;
  const offsetY = (height - contentHeight) / 2;

  // Násobič detailů mapy - vytvoří větší mapu (více tiles) pro více detailů
  const mapDetailMultiplier = mapSettings.mapDetailMultiplier ?? 1;
  
  // Fixní logické rozměry mapy - používají se pro Mapbox (konzistentní bez ohledu na viewport)
  // Pokud je mapDetailMultiplier > 1, vytvoříme větší mapu pro více detailů
  const logicalMapRect = useMemo(() => {
    return {
      width: styleDefinition.mapArea.width * mapDetailMultiplier,
      height: styleDefinition.mapArea.height * mapDetailMultiplier,
    };
  }, [styleDefinition.mapArea, mapDetailMultiplier]);

  // Vizuální rozměry mapy - používají se pro CSS positioning (mění se při resize)
  const mapRect = useMemo(() => {
    return {
      left: offsetX + styleDefinition.mapArea.x * scale,
      top: offsetY + styleDefinition.mapArea.y * scale,
      width: styleDefinition.mapArea.width * scale,
      height: styleDefinition.mapArea.height * scale,
    };
  }, [offsetX, offsetY, scale, styleDefinition.mapArea]);

  const textRefs = useRef<TextRefMap>({});
  const [backgroundWidths, setBackgroundWidths] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!styleDefinition.textBackgrounds || styleDefinition.textBackgrounds.length === 0) {
      if (Object.keys(backgroundWidths).length > 0) {
        setBackgroundWidths({});
      }
      return;
    }

    const nextWidths: Record<string, number> = {};
    styleDefinition.textBackgrounds.forEach((background) => {
      const baseWidth = background.width * scale;
      if (!background.dynamic) {
        nextWidths[background.id] = baseWidth;
        return;
      }

      const slots = background.slots?.length ? background.slots : SLOT_ORDER;
      let measured = 0;
      slots.forEach((slot) => {
        const block = styleDefinition.textBlocks.find((b) => b.slot === slot);
        if (!block) return;
        const ref = textRefs.current[block.id];
        if (ref) {
          measured = Math.max(measured, ref.scrollWidth);
        }
      });

      const padding = (background.padding ?? 0) * scale;
      const minWidth = (background.minWidth ?? background.width) * scale;
      const maxWidth =
        background.maxWidth !== undefined
          ? background.maxWidth * scale
          : Number.POSITIVE_INFINITY;

      const computed = Math.max(minWidth, Math.min(maxWidth, measured + padding || baseWidth));
      nextWidths[background.id] = computed;
    });

    setBackgroundWidths((prev) => {
      let changed = false;
      const merged: Record<string, number> = {};
      styleDefinition.textBackgrounds!.forEach((background) => {
        const value = nextWidths[background.id] ?? background.width * scale;
        merged[background.id] = value;
        if (!changed && prev[background.id] !== value) {
          changed = true;
        }
      });
      return changed ? merged : prev;
    });
  }, [scale, styleDefinition, textData, styleId]);

  const mapCenterX = mapRect.left + mapRect.width / 2;

  const getBackgroundLeft = (background: PosterTextBackground, widthPx: number) => {
    if (background.align === 'map-center') {
      return mapCenterX - widthPx / 2;
    }
    if (background.left !== undefined) {
      return offsetX + background.left * scale;
    }
    return mapRect.left;
  };

  const resolveTextValue = (block: PosterTextBlock) => {
    const slot = block.slot;
    const value = textData[slot] || '';
    if (value.trim().length === 0) {
      return block.defaultValue;
    }
    return block.uppercase ? value.toUpperCase() : value;
  };

  return (
    <div
      className="relative"
      style={{
        width,
        height,
        overflow: 'hidden',
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          left: offsetX,
          top: offsetY,
          width: contentWidth,
          height: contentHeight,
        }}
      >
        {/* Poster paper */}
        <div
          className={cn('absolute inset-0', styleDefinition.containerBackground ?? 'bg-white')}
        />

        {/* Map area - kontejner má vizuální rozměry, ale Mapbox má fixní logické rozměry */}
        <div
          className="absolute overflow-hidden"
          style={{
            left: mapRect.left - offsetX,
            top: mapRect.top - offsetY,
            width: mapRect.width,
            height: mapRect.height,
          }}
        >
          {/* Inner wrapper - škáluje Mapbox vizuálně, ale Mapbox má fixní rozměry */}
          {/* Pokud je mapDetailMultiplier > 1, zmenšíme mapu na správnou velikost pomocí scale */}
          <div
            style={{
              width: logicalMapRect.width,
              height: logicalMapRect.height,
              transform: `scale(${scale / mapDetailMultiplier})`,
              transformOrigin: 'top left',
            }}
          >
            <MapCanvas
              settings={mapSettings}
              width={logicalMapRect.width}
              onMapInstanceReady={onMapInstanceReady}
              height={logicalMapRect.height}
              mapIcons={mapIcons}
              selectedIconId={selectedIconId}
              selectedMapIconId={selectedMapIconId}
              selectedIconColor={selectedIconColor}
              mapScale={scale / mapDetailMultiplier}
              onMapChange={onMapChange}
              onMapClick={onMapClick}
              onIconClick={onIconClick}
              onIconUpdate={onIconUpdate}
              onIconSelectCancel={onIconSelectCancel}
              textElements={[]} // text render řeší PosterRenderer
              textData={textData}
              routes={routes}
              needsExport={false} // Není potřeba export pro běžné zobrazení
            />
          </div>
        </div>

        {/* Map borders */}
        {styleDefinition.mapBorders?.inner && (
          <div
            className="absolute pointer-events-none"
            style={{
              left:
                mapRect.left -
                offsetX -
                (styleDefinition.mapBorders.inner.expandX ?? 0) * scale,
              top:
                mapRect.top -
                offsetY -
                (styleDefinition.mapBorders.inner.expandY ?? 0) * scale,
              width:
                mapRect.width +
                2 * ((styleDefinition.mapBorders.inner.expandX ?? 0) * scale),
              height:
                mapRect.height +
                2 * ((styleDefinition.mapBorders.inner.expandY ?? 0) * scale),
              boxSizing: 'border-box',
              border: `${Math.max(1, styleDefinition.mapBorders.inner.width * scale)}px solid ${
                styleDefinition.mapBorders.inner.color
              }`,
            }}
          />
        )}

        {styleDefinition.mapBorders?.outer && (
          <div
            className="absolute pointer-events-none"
            style={{
              left:
                mapRect.left -
                offsetX -
                (styleDefinition.mapBorders.outer.expandX ?? 0) * scale,
              top:
                mapRect.top -
                offsetY -
                (styleDefinition.mapBorders.outer.expandY ?? 0) * scale,
              width:
                mapRect.width +
                2 * ((styleDefinition.mapBorders.outer.expandX ?? 0) * scale),
              height:
                mapRect.height +
                2 * ((styleDefinition.mapBorders.outer.expandY ?? 0) * scale),
              boxSizing: 'border-box',
              border: `${Math.max(1, styleDefinition.mapBorders.outer.width * scale)}px solid ${
                styleDefinition.mapBorders.outer.color
              }`,
            }}
          />
        )}

        {/* Text backgrounds */}
        {styleDefinition.textBackgrounds?.map((background) => {
          const bgWidth = backgroundWidths[background.id] ?? background.width * scale;
          return (
            <div
              key={background.id}
              className="absolute pointer-events-none"
              style={{
                left: getBackgroundLeft(background, bgWidth) - offsetX,
                top: background.top * scale,
                width: bgWidth,
                height: background.height * scale,
                borderRadius: background.radius ? background.radius * scale : undefined,
                background: background.background || '#f5f5f5',
              }}
            />
          );
        })}

        {/* Decorative lines */}
        {styleDefinition.decorativeLines?.map((line) => (
          <div
            key={line.id}
            className="absolute pointer-events-none"
            style={{
              left: line.left * scale,
              top: line.top * scale,
              width: line.width * scale,
              height: Math.max(1, line.strokeWidth * scale),
              background: line.color ?? '#000000',
              opacity: 0.9,
            }}
          />
        ))}

        {/* Text blocks */}
        {styleDefinition.textBlocks.map((block) => {
          const value = resolveTextValue(block);
          const transformParts: string[] = [];
          if (block.align === 'center') {
            transformParts.push('translateX(-50%)');
          } else if (block.align === 'right') {
            transformParts.push('translateX(-100%)');
          }

          const transform = transformParts.length > 0 ? transformParts.join(' ') : undefined;

          // Pro legenda styl použijeme dynamickou šířku s min/max hodnotami
          let blockWidth: number | string | undefined = block.width ? block.width * scale : undefined;
          let minWidth: number | undefined = undefined;
          let maxWidth: number | undefined = undefined;
          
            if (styleId === 'legenda' && styleDefinition.textBackgrounds && styleDefinition.textBackgrounds.length > 0) {
            const textBg = styleDefinition.textBackgrounds[0];
            if (textBg.dynamic && backgroundWidths[textBg.id]) {
              // Použijeme dynamickou šířku s min/max omezeními
              const padding = (textBg.padding ?? 0) * scale;
              const minBgWidth = (textBg.minWidth ?? textBg.width) * scale;
              const maxBgWidth = (textBg.maxWidth ?? textBg.width) * scale;
              
              // Pro text blocks použijeme auto width s min/max omezeními
              // Min width = min background width minus padding
              minWidth = minBgWidth - padding * 2;
              // Max width = max background width minus padding
              maxWidth = maxBgWidth - padding * 2;
              
              // Pro legenda styl použijeme auto width, aby se přizpůsobilo textu
              blockWidth = 'auto';
            }
          }

          // Formátování šířky pro style object
          const widthStyle = blockWidth 
            ? typeof blockWidth === 'number' 
              ? `${blockWidth}px` 
              : blockWidth
            : undefined;

          return (
            <div
              key={block.id}
              ref={(node) => {
                textRefs.current[block.id] = node;
              }}
              className="absolute text-black whitespace-nowrap pointer-events-none"
              style={{
                left: offsetX + block.x * scale,
                top: offsetY + block.y * scale,
                transform,
                width: widthStyle,
                minWidth: styleId === 'legenda' && minWidth !== undefined ? `${minWidth}px` : undefined,
                maxWidth: styleId === 'legenda' && maxWidth !== undefined ? `${maxWidth}px` : undefined,
                textAlign: block.align || 'left',
                fontFamily: block.fontFamily,
                fontWeight: block.fontWeight,
                fontSize: `${block.fontSize * scale}px`,
                lineHeight: block.lineHeight ? `${block.lineHeight * scale}px` : undefined,
                letterSpacing: block.letterSpacing
                  ? `${block.letterSpacing * scale}px`
                  : undefined,
                textTransform: block.uppercase ? 'uppercase' : undefined,
                color: block.color,
                overflow: styleId === 'legenda' ? 'visible' : 'hidden',
                textOverflow: styleId === 'legenda' ? undefined : 'ellipsis',
              }}
            >
              {value}
            </div>
          );
        })}
      </div>
    </div>
  );
}

